import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Plus,
  Megaphone,
  ArrowRight,
  Pencil,
  Trash2,
  Archive,
} from "lucide-react";
import { useAppServices } from "../../../hook/services";
import { useMarketplace } from "../../../marketplace/context";
import { useFeature, UpgradeBadge } from "../../../marketplace/FeatureGate";
import { Campaign } from "../../../types";
import HeroSection from "../../../components/ui/HeroSection";
import Button from "../../../components/ui/Button";
import EmptyState from "../../../components/ui/EmptyState";
import Skeleton from "../../../components/ui/Skeleton";
import Badge from "../../../components/ui/Badge";
import Pagination from "../../../components/ui/Pagination";
import { useConfirm } from "../../../components/ui/ConfirmDialog";
import { formatDate, formatNumber } from "../../../utils";
import CampaignFormModal from "./CampaignFormModal";

const PAGE_SIZE = 9;

const Campaigns = () => {
  const services = useAppServices();
  const confirm = useConfirm();
  const { buildPath, openUpgrade } = useMarketplace();
  const feat = useFeature("campaigns");

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [page, setPage] = useState(1);
  const paged = campaigns.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const fetchCampaigns = useCallback(async () => {
    if (!feat.allowed) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { response } = await services.campaigns.get();
    if (Array.isArray(response)) setCampaigns(response);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feat.allowed]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleSaved = () => {
    // Refetch so link_count + click_count reflect the new membership.
    setEditing(null);
    fetchCampaigns();
  };

  const handleDelete = async (c: Campaign) => {
    const ok = await confirm({
      title: `Delete "${c.name}"?`,
      message: "Links in this campaign will become uncategorized.",
      confirmLabel: "Delete campaign",
    });
    if (!ok) return;
    await services.campaigns.delete({ query: `_id=${c._id}` });
    setCampaigns((curr) => curr.filter((x) => x._id !== c._id));
  };

  // ----- Locked view for tiers without campaigns -----
  if (!feat.allowed) {
    return (
      <div className="space-y-6">
        <HeroSection
          eyebrow={
            <>
              <Megaphone size={11} className="text-brand-500" />
              <span>Campaigns</span>
            </>
          }
          title="Group links by campaign"
          description="Organize related short links into a campaign and see their combined click, geo, and device performance in one place."
        />
        <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card">
          <EmptyState
            icon={<Megaphone size={26} />}
            tone="amber"
            title="Campaigns are a Growth feature"
            description="Roll up clicks across multiple links, set per-campaign UTM defaults, and see which channels perform best."
            action={
              <div className="flex items-center gap-2">
                <UpgradeBadge feature="campaigns" />
                <Button
                  variant="gradient"
                  onClick={openUpgrade}
                  iconRight={<ArrowRight size={14} />}
                >
                  See plans
                </Button>
              </div>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <HeroSection
        eyebrow={
          <>
            <Megaphone size={11} className="text-brand-500" />
            <span>Campaigns</span>
          </>
        }
        title="Your campaigns"
        description="Group related links and see their combined performance."
        action={
          <Button
            variant="gradient"
            size="lg"
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
            icon={<Plus size={15} strokeWidth={2.5} />}
          >
            New campaign
          </Button>
        }
      />

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Skeleton className="h-[160px] rounded-2xl" />
          <Skeleton className="h-[160px] rounded-2xl" />
          <Skeleton className="h-[160px] rounded-2xl" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card">
          <EmptyState
            icon={<Megaphone size={26} />}
            tone="brand"
            title="No campaigns yet"
            description="Create your first campaign to roll up multiple links into a single dashboard."
            action={
              <Button
                variant="gradient"
                onClick={() => setModalOpen(true)}
                icon={<Plus size={14} strokeWidth={2.5} />}
              >
                Create a campaign
              </Button>
            }
          />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 anim-in">
            {paged.map((c) => (
              <CampaignCard
                key={c._id}
                campaign={c}
                detailHref={buildPath(`campaigns/${c._id}`)}
                onEdit={() => {
                  setEditing(c);
                  setModalOpen(true);
                }}
                onDelete={() => handleDelete(c)}
              />
            ))}
          </div>
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={campaigns.length}
            onPage={setPage}
          />
        </>
      )}

      <CampaignFormModal
        open={modalOpen}
        initial={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSaved={handleSaved}
      />
    </div>
  );
};

const CampaignCard = ({
  campaign,
  detailHref,
  onEdit,
  onDelete,
}: {
  campaign: Campaign;
  detailHref: string;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const linkCount = campaign.link_count ?? 0;
  const clickCount = campaign.click_count ?? 0;

  return (
    <div className="group relative bg-white border border-ink-200/70 rounded-2xl shadow-card hover:shadow-card-hover transition-all overflow-hidden">
      <div
        className="h-1.5 w-full"
        style={{ background: campaign.color }}
      />
      <RouterLink to={detailHref} className="block p-5">
        <div className="flex items-start justify-between gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: `${campaign.color}1a`, color: campaign.color }}
          >
            <Megaphone size={18} />
          </div>
          {campaign.archived && (
            <Badge tone="neutral" icon={<Archive size={9} />}>
              Archived
            </Badge>
          )}
        </div>

        <h3 className="mt-3 font-display font-semibold text-ink-900 text-base tracking-tight truncate">
          {campaign.name}
        </h3>
        {campaign.description && (
          <p className="mt-1 text-xs text-ink-500 line-clamp-2">
            {campaign.description}
          </p>
        )}

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
              Links
            </div>
            <div className="font-display font-semibold text-ink-900 text-lg tabular-nums">
              {formatNumber(linkCount)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
              Clicks
            </div>
            <div className="font-display font-semibold text-ink-900 text-lg tabular-nums">
              {formatNumber(clickCount)}
            </div>
          </div>
        </div>

        <div className="mt-3 text-[11px] text-ink-400">
          Created {formatDate(campaign.createdAt)}
        </div>
      </RouterLink>

      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
        <button
          onClick={(e) => {
            e.preventDefault();
            onEdit();
          }}
          className="w-7 h-7 rounded-md bg-white/90 backdrop-blur ring-1 ring-ink-200 text-ink-500 hover:text-ink-900 flex items-center justify-center shadow-sm"
          title="Edit"
        >
          <Pencil size={13} />
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            onDelete();
          }}
          className="w-7 h-7 rounded-md bg-white/90 backdrop-blur ring-1 ring-ink-200 text-ink-500 hover:text-rose-600 flex items-center justify-center shadow-sm"
          title="Delete"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
};

export default Campaigns;
